import { useState, useContext, useEffect } from "react"

import axios from "axios"

// components
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
} from "@material-ui/core"
import {
	KeyboardDatePicker,
	MuiPickersUtilsProvider,
} from "@material-ui/pickers"
import { SeasonSelector } from "../../../shared/SeasonSelector"
import { ButtonApiRequest } from "../../../shared/ButtonApiRequest"
import { HomeAwayToggle } from "../../../shared/HomeAwayToggle"
import { TransitionUp } from "../../../shared/ModalTransitions/TransitionUp"

// for the date picker
import "date-fns"
import DateFnsUtils from "@date-io/date-fns"

// types
import { Game } from "../../../interfaces/GameInterface"

// contexts
import { GlobalsContext } from "../../../context/GlobalsContext"
import { FeedbackContext } from "../../../context/FeedbackContext"
import { CompetitionsPageContext } from "../CompetitionsPage"

interface Props {
	open: boolean
	handleClose: () => void
	game: Game | null
}

export const EditGameModal = ({ open, handleClose, game }: Props) => {
	const { apiHeader, teamId } = useContext(GlobalsContext)
	const { alertSuccess, alertError } = useContext(FeedbackContext)
	const { refetchCompetitions } = useContext(CompetitionsPageContext)

	const [loading, setLoading] = useState<boolean>(false)

	const getDate = (d: string | Date) => {
		const date = new Date(d)
		date.setDate(date.getDate() + 1)
		return date
	}

	const [formValues, setFormValues] = useState<{ [key: string]: any }>({
		id: game ? game.id : 0,
		date: game ? getDate(game.date) : new Date(),
		home: game ? game.home_team.id === teamId : true,
		season: game ? game.season_id : "",
		opponent: game
			? game.home_team.id === teamId
				? game.away_team.id
				: game.home_team.id
			: "",
	})

	useEffect(() => {
		setFormValues({
			id: game ? game.id : 0,
			date: game ? getDate(game.date) : new Date(),
			home: game ? game.home_team.id === teamId : true,
			season: game ? game.season_id : "",
			opponent: game
				? game.home_team.id === teamId
					? game.away_team.id
					: game.home_team.id
				: "",
		})
	}, [game])

	const formatDate = (date: Date) => {
		return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
	}

	const editGame = async () => {
		setLoading(true)
		try {
			await axios.patch(
				`/api/game/${formValues.id}`,
				{
					home_team_id: formValues.home ? teamId : formValues.opponent,
					away_team_id: formValues.home ? formValues.opponent : teamId,
					season_id: formValues.season,
					date: formatDate(formValues.date),
				},
				apiHeader
			)
			handleClose()
			alertSuccess("Game saved!")
			refetchCompetitions()
		} catch (e) {
			alertError(
				"Something wen't wrong. Please refresh the page and try again."
			)
		}
		setLoading(false)
	}

	return (
		<div>
			<Dialog
				fullWidth
				maxWidth="sm"
				open={open}
				onClose={handleClose}
				TransitionComponent={TransitionUp}>
				<DialogTitle>Edit Game</DialogTitle>
				<DialogContent>
					<Grid container spacing={0}>
						<Grid item xs={12}>
							<MuiPickersUtilsProvider utils={DateFnsUtils}>
								<KeyboardDatePicker
									fullWidth
									required
									className="game-date-picker"
									disableToolbar
									variant="inline"
									format="MM/dd/yyyy"
									margin="normal"
									label="Date"
									autoOk={true}
									value={formValues.date}
									onChange={(date) => setFormValues({ ...formValues, date })}
									KeyboardButtonProps={{
										"aria-label": "change date",
									}}
								/>
							</MuiPickersUtilsProvider>
						</Grid>
						<Grid item xs={12} className="padding-top-12">
							<SeasonSelector
								defaultValue={formValues.season as number | undefined}
								required
								fullWidth
								variant="standard"
								onChange={(value: number) =>
									setFormValues({ ...formValues, season: value })
								}
							/>
						</Grid>
						<Grid item xs={12} className="padding-top-24">
							<HomeAwayToggle
								value={formValues.home ? "home" : "away"}
								onChange={(value: "home" | "away") =>
									setFormValues({ ...formValues, home: value === "home" })
								}
							/>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} color="primary">
						Cancel
					</Button>
					<ButtonApiRequest
						color="primary"
						disabled={
							!formValues.date || !formValues.opponent || !formValues.season
						}
						loading={loading}
						onClick={editGame}
						text="Save"
					/>
				</DialogActions>
			</Dialog>
		</div>
	)
}
